class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener(
      "keyup",
      (evt) => evt.code === "Escape" && this.close()
    );
    this.querySelector("#CartDrawer-Overlay").addEventListener(
      "click",
      this.close.bind(this)
    );
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    if (!cartLink) return;
    this.cartLink = cartLink;
    cartLink.setAttribute("aria-haspopup", "dialog");
    cartLink.setAttribute("aria-controls", "CartDrawer");
    cartLink.setAttribute("aria-expanded", "false");
    cartLink.addEventListener("click", (event) => {
      if (
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      )
        return;
      event.preventDefault();
      this.open(cartLink);
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    if (this.cartLink) this.cartLink.setAttribute("aria-expanded", "true");
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
      this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add("animate", "active");
    });

    let focusTrapped = false;
    const trapDrawerFocus = () => {
      if (focusTrapped || !this.classList.contains("active")) return;
      focusTrapped = true;
      const focusElement =
        this.querySelector(".drawer__inner") ||
        this.querySelector(".drawer__close");
      trapFocus(document.getElementById("CartDrawer"), focusElement);
    };
    this.addEventListener("transitionend", trapDrawerFocus, { once: true });
    // transitionend never fires when motion is reduced or the drawer is already open.
    setTimeout(trapDrawerFocus, 600);

    document.body.classList.add("overflow-hidden");
  }

  close() {
    this.classList.remove("active");
    if (this.cartLink) this.cartLink.setAttribute("aria-expanded", "false");
    removeTrapFocus(this.activeElement);
    document.body.classList.remove("overflow-hidden");
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute(
        "aria-controls",
        cartDrawerNote.nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute(
        "aria-expanded",
        !event.currentTarget.closest("details").hasAttribute("open")
      );
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.classList.remove("is-empty");
    this.querySelector("cart-drawer-items")?.classList.remove("is-empty");
    this.querySelector(".drawer__inner")?.classList.remove("is-empty");
    this.productId = parsedState.id;
    const hadVisibleNotice = Boolean(
      this.querySelector(".shipping-notice.loaded")
    );
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    // Keep shipping banner visible across HTML swaps (avoids opacity flicker).
    if (window.shippingThreshold) {
      window.shippingThreshold.markNoticesReady(this, {
        keepVisible: hadVisibleNotice,
      });
      window.shippingThreshold.sync({
        cartTotalCents: parsedState.total_price,
        animate: !hadVisibleNotice,
        preferNetwork: parsedState.total_price == null,
      });
    }

    setTimeout(() => {
      this.querySelector("#CartDrawer-Overlay").addEventListener(
        "click",
        this.close.bind(this)
      );
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      (event) => {
        // product-form already calls cart-drawer.renderContents();
        // cart-items / cart-subscription already patch drawer sections.
        if (
          event.source === "cart-items" ||
          event.source === "product-form" ||
          event.source === "cart-subscription"
        ) {
          return;
        }
        this.onCartUpdate();
      }
    );
  }

  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }

  onCartUpdate() {
    fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, "text/html");
        const sourceInner = html.querySelector(".drawer__inner");
        const targetInner = document.querySelector("#CartDrawer .drawer__inner");
        if (!sourceInner || !targetInner) return;

        const hadVisibleNotice = Boolean(
          targetInner.querySelector(".shipping-notice.loaded")
        );
        targetInner.innerHTML = sourceInner.innerHTML;

        if (window.shippingThreshold) {
          window.shippingThreshold.markNoticesReady(targetInner, {
            keepVisible: hadVisibleNotice,
          });
          window.shippingThreshold.sync({
            animate: !hadVisibleNotice,
            preferNetwork: true,
          });
        }

        const sourceDrawer = html.querySelector("cart-drawer");
        const targetDrawer = document.querySelector("cart-drawer");
        if (sourceDrawer && targetDrawer) {
          targetDrawer.classList.toggle(
            "is-empty",
            sourceDrawer.classList.contains("is-empty")
          );
        }

        const sourceItems = html.querySelector("cart-drawer-items");
        const targetItems = document.querySelector("cart-drawer-items");
        if (sourceItems && targetItems) {
          targetItems.classList.toggle(
            "is-empty",
            sourceItems.classList.contains("is-empty")
          );
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);
